// frontend/src/pages/VerifyLoginOtp.jsx

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "../axiosConfig";

const RESEND_COOLDOWN_SEC = 30;

export default function VerifyLoginOtp() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("verifyEmail");
    if (!stored) {
      navigate("/login");
    } else {
      setEmail(stored);
    }
  }, [navigate]);

  // Cooldown ticker
  useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const maskedEmail = useMemo(() => {
    if (!email) return "";
    const [user, domain] = email.split("@");
    if (!domain) return email;
    const u =
      user.length <= 2
        ? user[0] + "*"
        : user[0] + "*".repeat(Math.max(1, user.length - 2)) + user.slice(-1);
    return `${u}@${domain}`;
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setMessage("");
    setSubmitting(true);

    try {
      // Strip non-ASCII (without control range) then non-digits
      const cleanOtp = (otp || "")
        .replace(/[\u0080-\uFFFF]/g, "")
        .replace(/\D/g, "")
        .slice(0, 6);

      if (!/^[0-9]{6}$/.test(cleanOtp)) {
        setMessage("Please enter a valid 6-digit OTP.");
        setSubmitting(false);
        return;
      }
      const res = await axios.post("/verify-login-otp", { email, otp: cleanOtp });
      // ✅ Token for socket.io and authenticated API calls
      localStorage.setItem("token", res.data.token);
      setMessage("Login successful.");
      navigate("/dashboard");
    } catch (err) {
      const errMsg = err.response?.data?.error || "Invalid OTP. Please try again.";
      setMessage(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendLoading || cooldown > 0) return;
    setResendLoading(true);
    setMessage("");
    try {
      await axios.post("/resend-login-otp", { email });
      setMessage("OTP resent to your email.");
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      setMessage("Failed to resend OTP. Try again later.");
    } finally {
      setResendLoading(false);
    }
  };

  // Only allow ASCII digits; cap at 6; strip non-ASCII safely (no-control-regex friendly)
  const handleOtpChange = (v) => {
    const digits = v.replace(/[\u0080-\uFFFF]/g, "").replace(/\D/g, "").slice(0, 6);
    setOtp(digits);
  };

  // Support paste of entire code
  const onPaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").trim();
    const digits = pasted.replace(/[\u0080-\uFFFF]/g, "").replace(/\D/g, "").slice(0, 6);
    if (digits) {
      e.preventDefault();
      setOtp(digits);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-3 sm:px-4 py-6">
      {/* Autofill + dark-mode contrast fix */}
      <style>{`
        .dark input:-webkit-autofill,
        .dark input:-webkit-autofill:focus,
        .dark input:-webkit-autofill:hover {
          -webkit-text-fill-color: #e5e7eb; /* slate-200 */
          transition: background-color 5000s ease-in-out 0s;
          caret-color: #e5e7eb;
        }
        .dark input:-webkit-autofill::first-line { color: #e5e7eb; }
      `}</style>

      <motion.div
        className="w-full max-w-sm sm:max-w-md bg-white dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg p-4 sm:p-6 md:p-8"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-indigo-700 dark:text-indigo-300 mb-1.5 sm:mb-2">
          Verify OTP
        </h2>
        <p className="text-xs sm:text-sm text-center text-gray-700 dark:text-gray-300 mb-5 sm:mb-6">
          We sent a one-time code to <span className="font-medium">{maskedEmail}</span>
        </p>

        {message && (
          <div
            className={`text-center text-xs sm:text-sm p-2.5 sm:p-3 mb-3 sm:mb-4 rounded-md border
              ${/invalid|failed|error|expire|wrong|not/i.test(message)
                ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40"
                : "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/40"
            }`}
            role="alert"
            aria-live="polite"
          >
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          <div>
            <label className="block text-gray-800 dark:text-gray-200 text-xs sm:text-sm mb-1">
              OTP Code
            </label>
            <input
              type="tel"
              inputMode="numeric"
              pattern="^[0-9]{6}$"
              maxLength={6}
              autoFocus
              required
              value={otp}
              onChange={(e) => handleOtpChange(e.target.value)}
              onPaste={onPaste}
              className="w-full text-center tracking-widest text-lg sm:text-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/80 text-gray-900 dark:text-slate-100 placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 transition"
              placeholder="••••••"
              aria-label="6 digit one-time password"
            />
            <p className="mt-1 text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 text-center">
              Enter the 6-digit code.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting || otp.length !== 6}
            className={`w-full text-white text-sm sm:text-base font-semibold py-2.5 sm:py-3 rounded-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 dark:focus-visible:ring-indigo-500
              ${submitting || otp.length !== 6
                ? "bg-indigo-400 dark:bg-indigo-500 cursor-not-allowed"
                : "bg-indigo-700 hover:bg-indigo-800 dark:bg-indigo-600 dark:hover:bg-indigo-700"}`}
          >
            {submitting ? "Verifying…" : "Verify & Log In"}
          </button>
        </form>

        <div className="mt-5 sm:mt-6 text-center text-xs sm:text-sm text-gray-700 dark:text-gray-300">
          Didn’t receive a code?{" "}
          <button
            onClick={handleResend}
            disabled={resendLoading || cooldown > 0}
            className={`text-indigo-700 dark:text-indigo-400 hover:underline disabled:no-underline
              ${resendLoading || cooldown > 0 ? "opacity-60 cursor-default" : ""}`}
          >
            {resendLoading ? "Resending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
          </button>
        </div>

        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
          >
            Use a different email
          </button>
        </div>
      </motion.div>
    </div>
  );
}
